/**
 * Column Mapper Utility
 * Maps XLSX column headers to internal field names using flexible matching
 */
import { COLUMN_MAPPINGS } from "../types";
/**
 * Find the column index for a field using flexible matching
 * @param headers - Array of column headers from XLSX
 * @param fieldName - Internal field name to find
 * @returns Column index (0-based) or null if not found
 */
export function findColumnIndex(headers, fieldName) {
    const variations = COLUMN_MAPPINGS[fieldName] || [];
    for (let idx = 0; idx < headers.length; idx++) {
        const header = headers[idx];
        if (header && variations.includes(header.trim())) {
            return idx;
        }
    }
    return null;
}
/**
 * Create a complete column mapping from headers
 * @param headers - Array of column headers from XLSX
 * @returns Object mapping field names to column indices
 */
export function createColumnMapping(headers) {
    const mapping = {};
    for (const fieldName of Object.keys(COLUMN_MAPPINGS)) {
        mapping[fieldName] = findColumnIndex(headers, fieldName);
    }
    return mapping;
}
/**
 * Get the list of mapped columns with their header names
 * @param headers - Array of column headers from XLSX
 * @param mapping - Column mapping object
 * @returns Array of { fieldName, headerName, columnIndex } objects
 */
export function getMappedColumns(headers, mapping) {
    const mapped = [];
    for (const [fieldName, columnIndex] of Object.entries(mapping)) {
        if (columnIndex !== null) {
            mapped.push({
                fieldName,
                headerName: headers[columnIndex],
                columnIndex,
            });
        }
    }
    return mapped;
}
/**
 * Get the list of unmapped fields (fields that couldn't be found in headers)
 * @param mapping - Column mapping object
 * @returns Array of field names that are not mapped
 */
export function getUnmappedFields(mapping) {
    return Object.entries(mapping)
        .filter(([, columnIndex]) => columnIndex === null)
        .map(([fieldName]) => fieldName);
}
/**
 * Get required fields that are missing from the mapping
 * @param mapping - Column mapping object
 * @returns Array of required field names that are not mapped
 */
export function getMissingRequiredFields(mapping) {
    const requiredFields = ["lastName", "firstName", "email", "copyUserId"];
    return requiredFields.filter((field) => mapping[field] === null);
}
/**
 * Get a human-readable field name for display
 * @param fieldName - Internal field name
 * @returns Human-readable field name
 */
export function getFieldDisplayName(fieldName) {
    const displayNames = {
        lastName: "Last Name",
        firstName: "First Name",
        middleName: "Middle Name/Initial",
        suffix: "Suffix",
        npi: "NPI",
        username: "Username (UPN)",
        clinicalSystemId: "Clinical System ID Number",
        gender: "Gender",
        credentials: "Credentials",
        specialty: "Specialty",
        practiceName: "Practice Name",
        officeNumber: "Office Mainline Number",
        siteNames: "Site Name(s)",
        email: "Email Address",
        mobileNumber: "Mobile Number",
        mobileCarrier: "Mobile Carrier",
        pagerNumber: "Pager Number",
        pagerCarrier: "Pager Carrier",
        copyUserId: "Reference V5UserId",
        action: "Action",
        integrationMappings: "Integration Mappings",
    };
    return displayNames[fieldName] || fieldName;
}
/**
 * Check if a field is required
 * @param fieldName - Internal field name
 * @returns True if the field is required
 */
export function isRequiredField(fieldName) {
    const requiredFields = ["lastName", "firstName", "email", "copyUserId"];
    return requiredFields.includes(fieldName);
}
/**
 * Build a reverse mapping from original header strings to internal field names.
 * Used when reconstructing the results XLSX from the original headers.
 * @returns Object mapping header string -> internal field name
 */
export function buildHeaderToFieldMap() {
    const map = {};
    for (const [fieldName, headers] of Object.entries(COLUMN_MAPPINGS)) {
        for (const header of headers) {
            map[header] = fieldName;
        }
    }
    return map;
}
